import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import {
  IconAlertCircle,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChevronDown,
  IconEdit,
  IconFileImport,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { AccountTreeCanvas } from './AccountTreeCanvas';
import { useAccounts, useDeleteAccount } from './accountsApi';
import { AccountFormDrawer } from './AccountFormDrawer';
import { ImportChartModal } from './ImportChartModal';
import {
  ACCOUNT_TYPE,
  buildAccountTree,
  type AccountDto,
  type AccountNode,
  type AccountType,
} from './types';

// Phase Accounting-2: chart of accounts tree with operator CRUD. Add a
// sub-account under any row; rename/recode/deactivate operator accounts;
// system accounts only toggle active. Delete is soft and limited to
// operator leaves (server enforces all of this too).

export function ChartOfAccountsPage() {
  const { t, i18n } = useTranslation();
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useAccounts(showInactive);
  const deleteMutation = useDeleteAccount();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [parentFor, setParentFor] = useState<AccountDto | null>(null);
  const [editing, setEditing] = useState<AccountDto | null>(null);
  const [deleting, setDeleting] = useState<AccountDto | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const tree = useMemo(() => buildAccountTree(data ?? []), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tree;
    // When searching, flatten + filter, but keep the depth indent visible.
    const flat: AccountNode[] = [];
    const walk = (nodes: AccountNode[]) => {
      for (const n of nodes) {
        const matches =
          n.code.toLowerCase().includes(q) ||
          n.nameAr.toLowerCase().includes(q) ||
          (n.nameEn?.toLowerCase().includes(q) ?? false);
        if (matches) flat.push(n);
        walk(n.children);
      }
    };
    walk(tree);
    return flat;
  }, [tree, search]);

  // Table or pan/zoom canvas rendering of the same tree.
  const [view, setView] = useState<'table' | 'canvas'>('table');

  // Collapsed branch ids. Empty set = fully expanded (the default —
  // the Palestinian chart is small enough to scan whole).
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const toggleNode = (id: string) =>
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const collectParentIds = (nodes: AccountNode[]): string[] =>
    nodes.flatMap((n) =>
      n.children.length > 0 ? [n.accountId, ...collectParentIds(n.children)] : []);

  const expandAll = () => setCollapsedIds(new Set());
  const collapseAll = () => setCollapsedIds(new Set(collectParentIds(tree)));

  const openAddChild = (parent: AccountDto) => {
    setParentFor(parent);
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (account: AccountDto) => {
    setEditing(account);
    setParentFor(null);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.accountId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
      setDeleting(null);
    }
  };

  const renderRows = (nodes: AccountNode[]): JSX.Element[] =>
    nodes.flatMap((n) => [
      <AccountRow
        key={n.accountId}
        node={n}
        lang={i18n.language}
        isCollapsed={collapsedIds.has(n.accountId)}
        onToggle={toggleNode}
        onAddChild={openAddChild}
        onEdit={openEdit}
        onDelete={setDeleting}
      />,
      ...(collapsedIds.has(n.accountId) ? [] : renderRows(n.children)),
    ]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.chart.title')}
        subtitle={t('accounting.chart.subtitle')}
        actions={
          <Button
            variant="default"
            leftSection={<IconFileImport size={16} stroke={1.8} />}
            onClick={() => setImportOpen(true)}
          >
            {t('accounting.import.title')}
          </Button>
        }
      />

      <Paper p="md">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as 'table' | 'canvas')}
            data={[
              { value: 'table', label: t('accounting.chart.views.table') },
              { value: 'canvas', label: t('accounting.chart.views.canvas') },
            ]}
          />
          {view === 'table' && (
            // Search sits centered between the view toggle and the actions.
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <TextInput
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder={t('accounting.chart.searchPlaceholder')}
                leftSection={<IconSearch size={16} stroke={1.6} />}
                style={{ width: '100%', maxWidth: 420 }}
              />
            </div>
          )}
          <Group gap="xs" wrap="nowrap">
            {view === 'table' && (
              <>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  leftSection={<IconArrowsMaximize size={14} stroke={1.6} />}
                  onClick={expandAll}
                >
                  {t('accounting.chart.expandAll')}
                </Button>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  leftSection={<IconArrowsMinimize size={14} stroke={1.6} />}
                  onClick={collapseAll}
                >
                  {t('accounting.chart.collapseAll')}
                </Button>
              </>
            )}
            <Switch
              label={t('accounting.chart.includeInactive')}
              checked={showInactive}
              onChange={(e) => setShowInactive(e.currentTarget.checked)}
            />
          </Group>
        </Group>
      </Paper>

      {isLoading && (
        <Stack align="center" py={48}>
          <Loader size="sm" />
        </Stack>
      )}

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>
          {error instanceof Error ? error.message : t('common.toasts.errorBody')}
        </Alert>
      )}

      {!isLoading && !error && view === 'canvas' && (
        <AccountTreeCanvas tree={tree} onNodeClick={openEdit} />
      )}

      {!isLoading && !error && view === 'table' && (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.chart.columns.code')}</Table.Th>
                <Table.Th>{t('accounting.chart.columns.account')}</Table.Th>
                <Table.Th>{t('accounting.chart.columns.type')}</Table.Th>
                <Table.Th>{t('accounting.chart.columns.normalBalance')}</Table.Th>
                <Table.Th>{t('accounting.chart.columns.status')}</Table.Th>
                <Table.Th style={{ width: 120 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {search.trim()
                ? filtered.map((n) => (
                    <AccountRow
                      key={n.accountId}
                      node={n}
                      lang={i18n.language}
                      isCollapsed={false}
                      onToggle={toggleNode}
                      onAddChild={openAddChild}
                      onEdit={openEdit}
                      onDelete={setDeleting}
                    />
                  ))
                : renderRows(tree)}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <AccountFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        parent={parentFor}
        account={editing}
      />
      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('accounting.chart.delete.title')}
        message={t('accounting.chart.delete.message', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
      <ImportChartModal opened={importOpen} onClose={() => setImportOpen(false)} />
    </Stack>
  );
}

function AccountRow({
  node,
  lang,
  isCollapsed,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
}: {
  node: AccountNode;
  lang: string;
  isCollapsed: boolean;
  onToggle: (id: string) => void;
  onAddChild: (parent: AccountDto) => void;
  onEdit: (account: AccountDto) => void;
  onDelete: (account: AccountDto) => void;
}) {
  const { t } = useTranslation();
  const name = lang === 'ar' ? node.nameAr : (node.nameEn ?? node.nameAr);
  const deletable = !node.isSystem && node.children.length === 0;
  const hasChildren = node.children.length > 0;
  return (
    <Table.Tr style={{ opacity: node.isActive ? 1 : 0.55 }}>
      <Table.Td className="font-numeric" style={{ width: 110 }}>
        <Text size="sm" fw={node.isPostable ? 400 : 600}>
          {node.code}
        </Text>
      </Table.Td>
      <Table.Td>
        <div
          className="flex items-center gap-1"
          style={{ paddingInlineStart: node.depth * 22 }}
        >
          {hasChildren ? (
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray"
              onClick={() => onToggle(node.accountId)}
              aria-label={isCollapsed
                ? t('accounting.chart.expandAll')
                : t('accounting.chart.collapseAll')}
            >
              <IconChevronDown
                size={14}
                stroke={1.8}
                className={`transition-transform ${
                  isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''
                }`}
              />
            </ActionIcon>
          ) : (
            // Leaf marker keeps text aligned with sibling branch rows.
            <span className="inline-block w-[22px] text-center text-[var(--wheels-ink-muted)]">
              •
            </span>
          )}
          <Text size="sm" fw={node.isPostable ? 500 : 700}>
            {name}
          </Text>
          {hasChildren && isCollapsed && (
            <Badge size="xs" variant="light" color="gray" radius="sm" className="font-numeric">
              {countDescendants(node)}
            </Badge>
          )}
        </div>
      </Table.Td>
      <Table.Td>
        <Badge size="xs" variant="light" color={colorForType(node.accountType)} radius="sm">
          {t(typeLabel(node.accountType))}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="xs" c="dimmed">
          {node.normalBalance === 1
            ? t('accounting.chart.normalBalance.debit')
            : t('accounting.chart.normalBalance.credit')}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap={6}>
          {!node.isPostable && (
            <Badge size="xs" variant="default" radius="sm">
              {t('accounting.chart.headerOnly')}
            </Badge>
          )}
          {node.isSystem && (
            <Badge size="xs" variant="default" color="gray" radius="sm">
              {t('accounting.chart.system')}
            </Badge>
          )}
          {!node.isActive && (
            <Badge size="xs" variant="light" color="red" radius="sm">
              {t('accounting.chart.inactive')}
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <Tooltip label={t('accounting.chart.actions.addChild')}>
            <ActionIcon variant="subtle" onClick={() => onAddChild(node)}>
              <IconPlus size={16} stroke={1.6} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('accounting.chart.actions.edit')}>
            <ActionIcon variant="subtle" onClick={() => onEdit(node)}>
              <IconEdit size={16} stroke={1.6} />
            </ActionIcon>
          </Tooltip>
          {deletable && (
            <Tooltip label={t('accounting.chart.actions.delete')}>
              <ActionIcon variant="subtle" color="red" onClick={() => onDelete(node)}>
                <IconTrash size={16} stroke={1.6} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function countDescendants(node: AccountNode): number {
  return node.children.length +
    node.children.reduce((s, c) => s + countDescendants(c), 0);
}

function colorForType(type: AccountType): string {
  switch (type) {
    case ACCOUNT_TYPE.ASSET: return 'blue';
    case ACCOUNT_TYPE.LIABILITY: return 'orange';
    case ACCOUNT_TYPE.EQUITY: return 'violet';
    case ACCOUNT_TYPE.REVENUE: return 'green';
    case ACCOUNT_TYPE.EXPENSE: return 'red';
    default: return 'gray';
  }
}

function typeLabel(type: AccountType): string {
  switch (type) {
    case ACCOUNT_TYPE.ASSET: return 'accounting.chart.type.asset';
    case ACCOUNT_TYPE.LIABILITY: return 'accounting.chart.type.liability';
    case ACCOUNT_TYPE.EQUITY: return 'accounting.chart.type.equity';
    case ACCOUNT_TYPE.REVENUE: return 'accounting.chart.type.revenue';
    case ACCOUNT_TYPE.EXPENSE: return 'accounting.chart.type.expense';
    default: return 'accounting.chart.type.asset';
  }
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
